import {
  getInteractionFromFactory,
  ReconcileChange,
  ReconcileContext,
} from "../features/interaction_factory/InteractionFactory";
import { Prop } from "../models/Prop";
import Take3D from "../models/Take3D";
import { UsedProp } from "../models/UsedProp";
import Interaction from "../models/interactions/Interaction";
import { ISequence } from "../models/interactions/SequenceModel";

/**
 * Reconciles prefab names snapshotted in the training (prop presets and
 * Attach/ShowUIProp interactions) against the freshly fetched backend
 * metadata, which is the source of truth. Pure and identity-preserving:
 * anything untouched keeps its reference, and a no-op reconcile returns the
 * original arrays, so callers can detect change via `!==` and skip dispatches.
 * Props whose metadata is missing (failed fetch, deleted on the backend) are
 * left untouched — a prefab name is never blanked.
 */

export function buildPrefabNameByPropId(
  usedProps: UsedProp[]
): Record<string, string> {
  const result: Record<string, string> = {};
  usedProps.forEach((usedProp) => {
    if (usedProp.metadata.prefab_name)
      result[usedProp.metadata.id] = usedProp.metadata.prefab_name;
  });
  return result;
}

export function reconcilePropPresets(
  presets: Prop[],
  prefabNameByPropId: Record<string, string>,
  changes: ReconcileChange[]
): Prop[] {
  let changed = false;
  const result = presets.map((preset) => {
    const fresh = prefabNameByPropId[preset.propId];
    if (!fresh || fresh === preset.prefabName) return preset;
    changes.push({
      scope: "preset",
      id: preset.id,
      label: preset.name,
      field: "prefabName",
      oldValue: preset.prefabName,
      newValue: fresh,
    });
    changed = true;
    return { ...preset, prefabName: fresh };
  });
  return changed ? result : presets;
}

export function buildPrefabNameByInstanceId(
  presets: Prop[]
): Record<string, string> {
  const result: Record<string, string> = {};
  presets.forEach((preset) => {
    if (preset.prefabName) result[preset.id] = preset.prefabName;
  });
  return result;
}

export function reconcileInteraction(
  interaction: Interaction,
  context: ReconcileContext
): Interaction {
  const factoryEntity = getInteractionFromFactory(interaction.type);
  return factoryEntity?.Reconcile
    ? factoryEntity.Reconcile(interaction, context)
    : interaction;
}

export function reconcileTakes(
  takes: Take3D[],
  context: ReconcileContext
): Take3D[] {
  let anyTakeChanged = false;
  const result = takes.map((take) => {
    let changed = false;

    const interactions = take.interactions.map((interaction) => {
      const reconciled = reconcileInteraction(interaction, context);
      if (reconciled !== interaction) changed = true;
      return reconciled;
    });

    // the take-level sequence graph holds most Attach nodes in 3D flows
    const sequence = take.sequence
      ? (reconcileInteraction(take.sequence, context) as ISequence)
      : take.sequence;
    if (sequence !== take.sequence) changed = true;

    if (!changed) return take;
    anyTakeChanged = true;
    return { ...take, interactions, sequence };
  });
  return anyTakeChanged ? result : takes;
}

export function reconcileTrainingProps(
  presets: Prop[],
  takes: Take3D[],
  usedProps: UsedProp[]
): { presets: Prop[]; takes: Take3D[]; changes: ReconcileChange[] } {
  const changes: ReconcileChange[] = [];
  const prefabNameByPropId = buildPrefabNameByPropId(usedProps);
  const reconciledPresets = reconcilePropPresets(
    presets,
    prefabNameByPropId,
    changes
  );
  // build from ALL reconciled presets, not just changed ones, so interactions
  // stale relative to an already-correct preset also self-heal
  const context: ReconcileContext = {
    prefabNameByInstanceId: buildPrefabNameByInstanceId(reconciledPresets),
    changes,
  };
  const reconciledTakes = reconcileTakes(takes, context);
  return { presets: reconciledPresets, takes: reconciledTakes, changes };
}

export function logPrefabNameChanges(
  changes: ReconcileChange[],
  source: "load" | "sync"
): void {
  if (changes.length === 0) return;

  const byRename = new Map<string, { presets: number; references: number }>();
  changes.forEach((change) => {
    const key = `"${change.oldValue}" -> "${change.newValue}"`;
    const entry = byRename.get(key) ?? { presets: 0, references: 0 };
    if (change.scope === "preset") entry.presets += 1;
    else entry.references += 1;
    byRename.set(key, entry);
  });

  byRename.forEach((counts, rename) => {
    console.info(
      `[PrefabReconcile:${source}] ${rename} (${counts.presets} preset(s), ${counts.references} interaction reference(s))`
    );
  });
  console.info(`[PrefabReconcile:${source}] details:`, changes);
}
